import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Typography, Popover, Tooltip } from "antd";
import { EditOutlined, InfoCircleOutlined } from "@ant-design/icons";
import styled, { createGlobalStyle } from "styled-components";

import { GRAY_400, BLACK, GRAY_100 } from "../../constant/colors";
import {
  StyledActionButton,
  StyledActionButtonNoClick,
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription
} from "../../component/sharedComponents/settingsEntity/components";
import { StyledTable, TooltipGlobalStyle } from "../../component/sharedComponents/settingsEntity/table";
import { useColumnResizeAndReorder } from "@tailormed/common-client/component/ColumnResizeAndReorder/useColumnResizeAndReorder";
import { TableColumnWrapper } from "@tailormed/common-client/component/ColumnResizeAndReorder/TableColumnWrapper";
import { columnResizeAndReorderStyle } from "@tailormed/common-client/component/ColumnResizeAndReorder/columnResizeAndReorderStyle";
import { getUserName, dash, createSortTooltipGetter } from "../../component/sharedComponents/settingsEntity/utils";
import { selectUsers } from "../../store/selector";
import EmptyFacilityListIcon from "../../assets/svg/EmptyFacilityListIcon";
import { dateTimeFormatter } from "../../utils/date";
import { formatPhoneNumber } from "../../utils/formaters";

const { Text } = Typography;

export const FACILITY_LIST_PAGE_SIZE = 10;

// Table body height: with rows we leave room for the pagination bar; with no rows the empty state
// fills the card down to its bottom edge (Figma: Table = vertical fill, content centered).
const TABLE_BODY_HEIGHT = "calc(100vh - 400px)";
export const EMPTY_TABLE_BODY_HEIGHT = "calc(100vh - 340px)";

// Table column key → provider field used for sorting. Matching String is not sortable.
export const FACILITY_SORT_FIELDS = Object.freeze({
  nameNpi: "name"
});

// Column resize + drag-to-reorder — same common-client hook the dashboard
// tables use; layout persists per session under this key.
export const FACILITY_COLUMN_LAYOUT_KEY = "facilities-settings";
// Columns that render their own antd ellipsis tooltip — skip the hook's truncation tooltip.
const OWN_TOOLTIP_COLUMN_KEYS = ["nameNpi", "taxId", "address", "city", "matchingString"];

const FacilityTable = styled(StyledTable)`
  ${columnResizeAndReorderStyle}

  .ant-table {
    border: 1px solid ${GRAY_100};
    border-radius: 8px;
    overflow: hidden;
  }

  .ant-pagination {
    margin-top: 16px;
  }
`;

const NameNpiCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 220px;
`;

const NameText = styled(Text)`
  font-family: "Open Sans";
  font-size: 14px;
  font-weight: 600;
  line-height: 150%;
  color: ${BLACK};
  display: block;
  max-width: 220px;
`;

// Figma .Table/Cell First column: NPI sub-text is 14px Regular, Grey 400.
const NpiText = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: ${GRAY_400};
  line-height: 1.5;
`;

const CellText = styled(Text)`
  font-size: 14px;
  color: ${BLACK};
  display: block;
  max-width: 200px;
`;

/* antd 4 topRight + arrowPointAtCenter places the popover's right edge 20px past the target centre
   (horizontalArrowShift 16 + arrowWidth 4) but draws the 22px arrow box at right: 16px, so the arrow
   tip (16 + 11) ends up 7px left of the icon centre. right: 9px puts the tip at 20px. */
const InfoPopoverGlobalStyle = createGlobalStyle`
  .facility-info-popover.ant-popover-placement-topRight .ant-popover-arrow {
    right: 9px;
  }
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 8px;
`;

export const FacilityList = ({
  facilities,
  isSearchResult = false,
  currentPage = 1,
  onPageChange,
  sortColumn = "nameNpi",
  sortOrder = "ascend",
  onSortChange,
  hiddenColumns = [],
  canEdit = false,
  onEdit,
  onViewDetails
}) => {
  const { t } = useTranslation();
  const users = useSelector(selectUsers) || {};
  const { processColumns, components, containerRef, reorderGuideLeft, tableProps } = useColumnResizeAndReorder(
    FACILITY_COLUMN_LAYOUT_KEY,
    ["actions"],
    { iconColumnKeys: ["actions"], noTruncationTooltipKeys: OWN_TOOLTIP_COLUMN_KEYS }
  );

  const getSortTooltip = createSortTooltipGetter(t, "facility_management.list", sortColumn, sortOrder);

  // Same sentence structure as the detail page ("… on {date} by {user}"); when the acting user
  // can't be resolved we show only the date — no "User" placeholder.
  const getUpdateInfo = (facility) => {
    if (facility.updatedBy && facility.userUpdatedAt) {
      const user = getUserName(users, facility.updatedBy, null);
      const date = dateTimeFormatter(facility.userUpdatedAt);
      return user
        ? t("facility_management.list.last_updated_by", { user, date })
        : t("facility_management.list.last_updated_on", { date });
    }
    if (facility.createdAt) {
      const user = getUserName(users, facility.createdBy, null);
      const date = dateTimeFormatter(facility.createdAt);
      return user
        ? t("facility_management.list.created_by", { user, date })
        : t("facility_management.list.created_on", { date });
    }
    return t("facility_management.list.no_update_information");
  };

  const sortableTitle = (columnKey, labelKey) => () =>
    (
      <Tooltip title={getSortTooltip(columnKey)} placement="top" overlayClassName="settings-sort-tooltip">
        <span>{t(labelKey)}</span>
      </Tooltip>
    );

  const allColumns = useMemo(
    () => [
      {
        title: sortableTitle("nameNpi", "facility_management.list.name_npi"),
        dataIndex: "name",
        key: "nameNpi",
        width: 240,
        sorter: true,
        sortOrder: sortColumn === "nameNpi" ? sortOrder : null,
        showSorterTooltip: false,
        render: (name, record) => (
          <NameNpiCell>
            <NameText ellipsis={{ tooltip: name }}>{dash(name)}</NameText>
            <NpiText>{t("facility_management.list.npi_prefix", { npi: dash(record.npi) })}</NpiText>
          </NameNpiCell>
        )
      },
      {
        title: t("facility_management.list.tax_id"),
        dataIndex: "taxId",
        key: "taxId",
        width: 120,
        render: (taxId) => <CellText ellipsis={{ tooltip: taxId }}>{dash(taxId)}</CellText>
      },
      {
        title: t("facility_management.list.address"),
        dataIndex: "address",
        key: "address",
        width: 200,
        render: (address) => <CellText ellipsis={{ tooltip: address }}>{dash(address)}</CellText>
      },
      {
        title: t("facility_management.list.suite_number"),
        dataIndex: "suiteNumber",
        key: "suiteNumber",
        width: 120,
        render: (suiteNumber) => dash(suiteNumber)
      },
      {
        title: t("facility_management.list.city"),
        dataIndex: "city",
        key: "city",
        width: 130,
        render: (city) => <CellText ellipsis={{ tooltip: city }}>{dash(city)}</CellText>
      },
      {
        title: t("facility_management.list.state"),
        dataIndex: "state",
        key: "state",
        width: 80,
        render: (state) => dash(state)
      },
      {
        title: t("facility_management.list.phone"),
        dataIndex: "phone",
        key: "phone",
        width: 140,
        render: (phone) => (phone ? formatPhoneNumber(phone) : "-")
      },
      {
        title: t("facility_management.list.zip_code"),
        dataIndex: "zipCode",
        key: "zipCode",
        width: 100,
        render: (zipCode) => dash(zipCode)
      },
      {
        title: t("facility_management.list.matching_string"),
        dataIndex: "providerMatchingAliases",
        key: "matchingString",
        width: 180,
        render: (aliases) => <CellText ellipsis={{ tooltip: aliases }}>{dash(aliases)}</CellText>
      },
      {
        title: t("facility_management.list.actions"),
        key: "actions",
        width: 100,
        render: (_, record) => (
          <ActionsCell>
            {canEdit && (
              <Popover
                content={t("facility_management.list.edit_tooltip")}
                overlayInnerStyle={{ borderRadius: "8px" }}
                arrowPointAtCenter
              >
                <StyledActionButton
                  id={`settings-facilities-edit-icon-${record.id}`}
                  aria-label={t("facility_management.list.edit_tooltip")}
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(record);
                  }}
                />
              </Popover>
            )}
            {/* Figma tooltip variant "Arrow=Bottom right": anchored to the right edge so the
                viewport overflow adjustment never shifts it; the arrow points at the icon's center. */}
            <Popover
              content={getUpdateInfo(record)}
              overlayInnerStyle={{ padding: "6px 8px !important", borderRadius: "8px" }}
              placement="topRight"
              overlayClassName="facility-info-popover"
              arrowPointAtCenter
            >
              <StyledActionButtonNoClick
                id={`Facilities_Info_${record.id}`}
                aria-label={getUpdateInfo(record)}
                type="primary"
                icon={<InfoCircleOutlined />}
                onClick={(e) => e.stopPropagation()}
              />
            </Popover>
          </ActionsCell>
        )
      }
    ],
    [sortColumn, sortOrder, canEdit, users, t]
  );

  const columns = useMemo(
    () => allColumns.filter((column) => column.key === "actions" || !hiddenColumns.includes(column.key)),
    [allColumns, hiddenColumns]
  );

  const handleTableChange = (pagination, filters, sorter, extra) => {
    // Table onChange also fires on pagination; reporting a "sort" then would reset currentPage to 1.
    if (extra?.action === "sort" && onSortChange) {
      onSortChange(sorter?.order ? sorter.columnKey : null, sorter?.order || null);
    }
  };

  const emptyState = () => (
    <EmptyStateContainer $gap="8px" $fill={EMPTY_TABLE_BODY_HEIGHT}>
      <EmptyStateIcon $size="48px" $noMargin>
        <EmptyFacilityListIcon />
      </EmptyStateIcon>
      <EmptyStateTitle>
        {isSearchResult
          ? t("facility_management.list.search_empty_title")
          : t("facility_management.list.empty_state_title")}
      </EmptyStateTitle>
      <EmptyStateDescription>
        {isSearchResult
          ? t("facility_management.list.search_empty_description")
          : t("facility_management.list.empty_state_description")}
      </EmptyStateDescription>
    </EmptyStateContainer>
  );

  return (
    <>
      <TooltipGlobalStyle />
      <InfoPopoverGlobalStyle />
      <TableColumnWrapper containerRef={containerRef} reorderGuideLeft={reorderGuideLeft}>
        <FacilityTable
          {...tableProps}
          components={components}
          id="Facilities_Table"
          columns={processColumns(columns)}
          dataSource={facilities}
          rowKey="id"
          showSorterTooltip={{ target: "sorter-icon" }}
          onRow={(record) => ({
            onClick: () => onViewDetails(record),
            onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onViewDetails(record);
              }
            },
            tabIndex: 0,
            id: `settings-facilities-table-row-${record.id}`
          })}
          onChange={handleTableChange}
          pagination={
            facilities.length === 0
              ? false
              : {
                  current: currentPage,
                  pageSize: FACILITY_LIST_PAGE_SIZE,
                  total: facilities.length,
                  showSizeChanger: false,
                  hideOnSinglePage: false,
                  showTotal: (total, range) => (
                    <span>
                      {t("facility_management.list.pagination_showing", {
                        range_start: range[0],
                        range_end: range[1],
                        total
                      })}{" "}
                      <strong>{t("facility_management.list.facilities_label")}</strong>
                    </span>
                  ),
                  onChange: (page) => onPageChange && onPageChange(page),
                  position: ["bottomLeft"]
                }
          }
          locale={{ emptyText: emptyState() }}
          scroll={{ y: facilities.length === 0 ? EMPTY_TABLE_BODY_HEIGHT : TABLE_BODY_HEIGHT }}
          sticky
        />
      </TableColumnWrapper>
    </>
  );
};

export default FacilityList;
